// src/pages/PartnerWaitlist.tsx
'use client';

import React, { useState, useMemo } from 'react';
import { Link } from "react-router-dom";
import { ChevronLeft } from 'lucide-react';
import { getSupabase } from "@/lib/supabase";

export default function PartnerWaitlist() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    business: '',
    clients: '',
    instagram: '',
    location: '',
    referral: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const isFormValid = useMemo(() => {
    return (
      form.name.trim().length > 0 &&
      form.email.trim().length > 0 &&
      form.business.trim().length > 0 &&
      form.clients.trim().length > 0
    );
  }, [form.name, form.email, form.business, form.clients]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    setLoading(true);
    const supabase = getSupabase();
    const { error } = await supabase.from('partner_waitlist').insert({
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      business_type: form.business.trim(),
      client_count: Number(form.clients),
      instagram: form.instagram.trim() || null,
      location: form.location.trim() || null,
      referral_source: form.referral.trim() || null,
      notes: form.notes.trim() || null,
      joined_at: new Date().toISOString(),
    });

    if (!error) setSuccess(true);
    else alert('Error – try again or DM @jamesstrudwick on Instagram');
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#ffffff] text-[#0f172a]">
        <div className="fixed inset-0 -z-10 bg-gradient-to-b from-[#f8fafc] to-[#ffffff]" />

        <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-[#e2e8f0]">
          <div className="max-w-screen-xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-gray-500 hover:text-[#a78bfa] transition-colors">
              <ChevronLeft className="w-5 h-5" />
              Back to Home
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#a78bfa] flex items-center justify-center">
                <svg className="w-5 h-5 text-[#0f172a]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span className="font-semibold text-[#0f172a]">Input</span>
            </div>
          </div>
        </header>

        <main className="flex items-center justify-center min-h-[80vh] p-6">
          <div className="text-center max-w-2xl">
            <div className="w-24 h-24 bg-[#a78bfa] rounded-full flex items-center justify-center mx-auto mb-8">
              <svg className="w-12 h-12 text-[#0f172a]" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6">You're In</h1>
            <p className="text-2xl md:text-3xl font-bold text-[#a78bfa] mb-4">
              Founding Partner Status Locked
            </p>
            <p className="text-lg text-gray-600 mb-8">
              Lifetime 20% revenue share + free onboarding + priority support
            </p>
            <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-8 inline-block">
              <p className="text-gray-500 mb-2">Launching</p>
              <p className="text-4xl md:text-5xl font-bold text-[#a78bfa]">Early 2026</p>
            </div>
            <p className="text-gray-500 mt-8">
              We'll be in touch soon with next steps.
            </p>
          </div>
        </main>

        <footer className="py-8 text-center text-gray-500 text-sm border-t border-[#e2e8f0]">
          © {new Date().getFullYear()} Input
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#ffffff] text-[#0f172a]">
      <div className="fixed inset-0 -z-10 bg-gradient-to-b from-[#f8fafc] to-[#ffffff]" />

      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-[#e2e8f0]">
        <div className="max-w-screen-xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-500 hover:text-[#a78bfa] transition-colors">
            <ChevronLeft className="w-5 h-5" />
            Back to Home
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#a78bfa] flex items-center justify-center">
              <svg className="w-5 h-5 text-[#0f172a]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span className="font-semibold text-[#0f172a]">Input</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 md:py-20">
        <h1 className="text-4xl md:text-5xl font-bold text-center mb-4">
          Partner Program
        </h1>
        <p className="text-xl text-[#a78bfa] text-center font-semibold mb-2">
          Launching Early 2026
        </p>
        <p className="text-lg text-gray-600 text-center mb-12 max-w-2xl mx-auto">
          Be the first coach, gym, or clinic to earn recurring revenue with Input
        </p>

        {/* Benefits */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-6 text-center">
            <div className="text-4xl mb-3">💰</div>
            <h3 className="text-lg font-bold mb-2">40% Revenue Share</h3>
            <p className="text-gray-500 text-sm">Lifetime rate for founding partners</p>
          </div>
          <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-6 text-center">
            <div className="text-4xl mb-3">🚀</div>
            <h3 className="text-lg font-bold mb-2">Free Onboarding</h3>
            <p className="text-gray-500 text-sm">We'll set everything up for you</p>
          </div>
          <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-6 text-center">
            <div className="text-4xl mb-3">⭐</div>
            <h3 className="text-lg font-bold mb-2">Priority Support</h3>
            <p className="text-gray-500 text-sm">Direct line to the founding team</p>
          </div>
        </div>

        {/* Form */}
        <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-8 max-w-xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-6">Claim Your Spot</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              required
              placeholder="Your Name *"
              value={form.name}
              onChange={e => setForm({...form, name: e.target.value})}
              className="w-full px-4 py-4 rounded-xl border border-[#e2e8f0] bg-[#e2e8f0] text-[#0f172a] placeholder-gray-500 focus:border-[#a78bfa] focus:outline-none transition-colors"
            />
            <input
              required
              type="email"
              placeholder="Email Address *"
              value={form.email}
              onChange={e => setForm({...form, email: e.target.value})}
              className="w-full px-4 py-4 rounded-xl border border-[#e2e8f0] bg-[#e2e8f0] text-[#0f172a] placeholder-gray-500 focus:border-[#a78bfa] focus:outline-none transition-colors"
            />
            <input
              required
              placeholder="Business / Brand Name *"
              value={form.business}
              onChange={e => setForm({...form, business: e.target.value})}
              className="w-full px-4 py-4 rounded-xl border border-[#e2e8f0] bg-[#e2e8f0] text-[#0f172a] placeholder-gray-500 focus:border-[#a78bfa] focus:outline-none transition-colors"
            />
            <input
              required
              type="number"
              placeholder="Approx. clients per month *"
              value={form.clients}
              onChange={e => setForm({...form, clients: e.target.value})}
              className="w-full px-4 py-4 rounded-xl border border-[#e2e8f0] bg-[#e2e8f0] text-[#0f172a] placeholder-gray-500 focus:border-[#a78bfa] focus:outline-none transition-colors"
            />
            
            <div className="grid grid-cols-2 gap-4">
              <input
                placeholder="Instagram @handle"
                value={form.instagram}
                onChange={e => setForm({...form, instagram: e.target.value})}
                className="w-full px-4 py-4 rounded-xl border border-[#e2e8f0] bg-[#e2e8f0] text-[#0f172a] placeholder-gray-500 focus:border-[#a78bfa] focus:outline-none transition-colors"
              />
              <input
                placeholder="Location (City)"
                value={form.location}
                onChange={e => setForm({...form, location: e.target.value})}
                className="w-full px-4 py-4 rounded-xl border border-[#e2e8f0] bg-[#e2e8f0] text-[#0f172a] placeholder-gray-500 focus:border-[#a78bfa] focus:outline-none transition-colors"
              />
            </div>

            <input
              placeholder="How did you hear about us?"
              value={form.referral}
              onChange={e => setForm({...form, referral: e.target.value})}
              className="w-full px-4 py-4 rounded-xl border border-[#e2e8f0] bg-[#e2e8f0] text-[#0f172a] placeholder-gray-500 focus:border-[#a78bfa] focus:outline-none transition-colors"
            />
            <textarea
              placeholder="Anything else? (e.g. I train pro athletes)"
              rows={3}
              value={form.notes}
              onChange={e => setForm({...form, notes: e.target.value})}
              className="w-full px-4 py-4 rounded-xl border border-[#e2e8f0] bg-[#e2e8f0] text-[#0f172a] placeholder-gray-500 focus:border-[#a78bfa] focus:outline-none transition-colors resize-none"
            />

            <button
              type="submit"
              disabled={loading || !isFormValid}
              className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
                isFormValid
                  ? 'bg-[#a78bfa] hover:bg-[#8b5cf6] text-[#0f172a] cursor-pointer shadow-lg shadow-[#a78bfa]/30'
                  : 'bg-[#f1f5f9] text-gray-500 cursor-not-allowed'
              }`}
            >
              {loading ? "Securing Your Spot..." : "Claim Founding Partner Status"}
            </button>
          </form>
        </div>
      </main>

      <footer className="py-8 text-center text-gray-500 text-sm border-t border-[#e2e8f0]">
        © {new Date().getFullYear()} Input
      </footer>
    </div>
  );
}
